import { useCallback, useEffect, useState } from 'react';
import Dropdown from '../../components/Dropdown';
import Field from '../../components/Field';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import { ErrorBanner, InfoBanner, Toast } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as codeRules from '../../validation/codeRules';
import { api } from '../../api/client';

const EMPTY_PAGE = { items: [], page: 0, totalItems: 0, hasNext: false };

const CODE_STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'UNUSED', label: 'Unused' },
  { value: 'ALLOCATED', label: 'Allocated' },
  { value: 'USED', label: 'Used' },
];

/**
 * Admin view: mint codes, watch where they went, and take an export.
 *
 * <p>The export sends whatever filter is on screen, so the CSV and the table always
 * agree — which matters because the server records that filter against the download.
 */
export default function AdminCodesPage() {
  const [page, setPage] = useState(EMPTY_PAGE);
  const [batches, setBatches] = useState([]);
  // The inventory row already names every active title, so it doubles as the list
  // behind the title filter and the generate form — one request instead of two.
  const [inventory, setInventory] = useState([]);
  const [filters, setFilters] = useState({ titleId: '', batchId: '', status: '', query: '' });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const loadReference = useCallback(async () => {
    const [batchPage, stock] = await Promise.all([
      api.codeBatches({ size: 50 }),
      api.codeInventory(),
    ]);
    setBatches(batchPage.items);
    setInventory(stock);
  }, []);

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        setPage(await api.listCodes({ ...filters, page: pageNumber, size: 20 }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    loadReference().catch(setError);
  }, [loadReference]);

  useEffect(() => {
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  function setFilter(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
  }

  async function exportCsv() {
    setExporting(true);
    setError(null);
    try {
      const rows = await api.exportCodes(filters);
      setToast(`${rows} code(s) exported — the download is recorded against your account`);
    } catch (err) {
      setError(err);
    } finally {
      setExporting(false);
    }
  }

  const unallocated = inventory.reduce((total, row) => total + row.unallocated, 0);

  // The inventory and batch rows carry their own field names, so they are mapped to
  // options here rather than teaching the dropdown a third shape.
  const titleOptions = inventory.map((row) => ({ value: row.titleId, label: row.titleName }));
  const batchOptions = batches.map((batch) => ({ value: batch.id, label: batch.reference }));

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Activation Codes</h1>
          <p>
            Codes are minted at random and checked for collisions before they are stored.
            A code moves Unused → Allocated → Used and never back.
          </p>
        </div>
        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            disabled={inventory.length === 0}
            onClick={() => setShowGenerate(true)}
          >
            <Icon name="plus" size={16} /> Generate codes
          </button>
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="stat-row">
          <div className="stat">
            <b>{unallocated.toLocaleString()}</b>
            <span>Unallocated</span>
          </div>
          {inventory.slice(0, 5).map((row) => (
            <div className="stat stat-quiet" key={row.titleId}>
              <b>{row.unallocated.toLocaleString()}</b>
              <span title={row.titleName}>{row.shortName}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="toolbar">
          <input
            className="input"
            style={{ flex: '1 1 200px' }}
            placeholder="Search by code"
            aria-label="Search by code"
            maxLength={20}
            value={filters.query}
            onChange={(event) => setFilter('query', event.target.value)}
          />
          <div style={{ flex: '0 1 180px' }}>
            <Dropdown
              ariaLabel="Filter by title"
              options={titleOptions}
              value={filters.titleId}
              onChange={(titleId) => setFilter('titleId', titleId)}
              placeholder="All titles"
            />
          </div>
          <div style={{ flex: '0 1 180px' }}>
            <Dropdown
              ariaLabel="Filter by batch"
              options={batchOptions}
              value={filters.batchId}
              onChange={(batchId) => setFilter('batchId', batchId)}
              placeholder="All batches"
            />
          </div>
          <div style={{ flex: '0 1 150px' }}>
            <Dropdown
              ariaLabel="Filter by status"
              options={CODE_STATUSES}
              value={filters.status}
              onChange={(status) => setFilter('status', status)}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exporting || page.totalItems === 0}
            onClick={exportCsv}
          >
            <Icon name="download" size={16} /> {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Batch</th>
                <th>Status</th>
                <th>School</th>
                <th>Used by</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((code) => (
                <tr key={code.id}>
                  <td className="code-text">{code.code}</td>
                  <td>{code.titleName}</td>
                  <td className="mono">{code.batchReference}</td>
                  <td>
                    <StatusBadge status={code.status} />
                  </td>
                  <td>{code.schoolName ?? <span className="muted">—</span>}</td>
                  <td>{code.usedByName ?? <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && page.items.length === 0 && <div className="empty">No codes match.</div>}

        <div
          className="toolbar"
          style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}
        >
          <span className="muted small">{page.totalItems.toLocaleString()} code(s)</span>
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page.page === 0}
              onClick={() => load(page.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={!page.hasNext}
              onClick={() => load(page.page + 1)}
            >
              Next
            </button>
          </span>
        </div>
      </section>

      {inventory.length === 0 && (
        <InfoBanner>
          There are no titles in the catalogue yet, and codes are always minted for a
          title. Add one in the <b>Content Library</b> first.
        </InfoBanner>
      )}

      {showGenerate && (
        <GenerateModal
          titles={inventory}
          onClose={() => setShowGenerate(false)}
          onDone={async (batch) => {
            setShowGenerate(false);
            setToast(`${batch.quantity} code(s) minted as ${batch.reference}`);
            await loadReference();
            await load(0);
          }}
        />
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

function GenerateModal({ titles, onClose, onDone }) {
  const [error, setError] = useState(null);
  const form = useForm({
    initialValues: { titleId: '', quantity: '100', reference: '' },
    validators: {
      titleId: codeRules.titleId,
      quantity: codeRules.quantity,
      reference: codeRules.reference,
    },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      onDone(
        await api.generateCodes({
          titleId: values.titleId,
          quantity: Number(values.quantity),
          reference: values.reference.trim() || undefined,
        }),
      );
    } catch (err) {
      if (!form.applyServerErrors(err, codeRules.SERVER_CODES)) setError(err);
    }
  });

  return (
    <Modal
      title="Generate activation codes"
      subtitle="Codes are minted for one title and start life unallocated."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="generate-codes" className="btn btn-primary" disabled={form.submitting}>
            {form.submitting ? 'Generating…' : 'Generate'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      <form id="generate-codes" className="stack" {...form.formProps} onSubmit={submit}>
        <Field label="Title" id="titleId" required error={form.errorFor('titleId')}>
          <Dropdown
            id="titleId"
            required
            options={titles.map((title) => ({ value: title.titleId, label: title.titleName }))}
            value={form.values.titleId}
            onChange={(titleId) => form.setValue('titleId', titleId)}
            placeholder="Select a title"
            error={form.errorFor('titleId')}
          />
        </Field>

        <Field
          label="Quantity"
          id="quantity"
          required
          error={form.errorFor('quantity')}
          hint={`Up to ${codeRules.LIMITS.batch.toLocaleString()} in one run`}
        >
          <input className="input" inputMode="numeric" maxLength={5} {...form.field('quantity')} />
        </Field>

        <Field
          label="Batch reference"
          id="reference"
          error={form.errorFor('reference')}
          hint="Leave blank to derive one from the title code and today's date"
        >
          <input className="input" maxLength={codeRules.LIMITS.reference} {...form.field('reference')} />
        </Field>
      </form>
    </Modal>
  );
}
